import React from 'react';
import { displayPriceFormat, getActiveUserPromotion, roundNumber } from '../../../utils';
import { useAppSelector } from '../../../redux/hook';
import {
  DISHOP_GREEN,
  MODIFY_ORDER_TYPE_PROMOTION,
  ORDER_TYPE_DELIVERY,
  PROMOTION_DELIVERY_FREE,
  PROMOTION_PERCENTAGE,
  UNIT,
} from '../../../constants';
import OrderPromotion from './OrderPromotion';
import Spinner from '../../Spinner';
import SkeletonRow from '../../Skeleton/SkeletonRow.view';
import { getTitleFromOffer } from '../../ThresholdMarkertingProgress/ThresholdMarketingProgress.service';
import DashedLine from '../../DashedLine/DashedLine';

export const getDiscountedTotals = ({
  totalProductPriceOrder,
  marketingCampaignPromoAmount,
}: {
  totalProductPriceOrder: number;
  marketingCampaignPromoAmount?: number;
}) => {
  const resolvedProductTotal = Number(totalProductPriceOrder);
  const resolvedMarketingPromoAmount = Number(marketingCampaignPromoAmount);

  return {
    subtotalBeforeDiscount: Number.isFinite(resolvedProductTotal)
      ? resolvedProductTotal
      : 0,
    remainingMarketingDiscountAmount:
      Number.isFinite(resolvedMarketingPromoAmount) &&
      resolvedMarketingPromoAmount > 0
        ? Math.max(0, roundNumber(resolvedMarketingPromoAmount))
        : 0,
  };
};

type OrderTotalProps = {
  commande?: any;
  promotion?: any;
  showFees: boolean;
  isLoading?: boolean;
  marketingCampaignPromoAmount?: number;
  totalPriceOrder: number;
  isOrderFreeDelivery?: boolean;
  managementFee: number;
  totalProductPriceOrder: number;
  orderUserServicePrice?: number;
};

export default function OrderTotal({
  commande,
  promotion,
  showFees,
  isLoading = false,
  marketingCampaignPromoAmount,
  totalPriceOrder,
  isOrderFreeDelivery = false,
  managementFee = 0,
  totalProductPriceOrder,
  orderUserServicePrice,
}: OrderTotalProps) {
  const {
    address = {},
    orderType,
    freeDelivery,
  } = useAppSelector((state) => state.pendingOrderReducer);

  const { subtotalBeforeDiscount, remainingMarketingDiscountAmount } =
    getDiscountedTotals({
      totalProductPriceOrder,
      marketingCampaignPromoAmount,
    });

  const { thresholdOffer } = useAppSelector((state) => state.offerReducer);

  const isSubmittedOrder = !!commande;

  const userServicePrice = isSubmittedOrder
    ? (orderUserServicePrice ?? 0)
    : (address?.deliveryPrice ?? 0);

  const displayPriceAsync = (price: number) =>
    isLoading ? <Spinner /> : displayPriceFormat(price, false);
  const deliveryIsFree =
    (promotion && promotion.type === PROMOTION_DELIVERY_FREE) ||
    freeDelivery ||
    isOrderFreeDelivery;

  const subtotalAfterDiscount = Math.max(
    0,
    roundNumber(
      totalPriceOrder -
        managementFee -
        (deliveryIsFree ? 0 : userServicePrice),
    ),
  );

  const isClickAndCollectPromo =
    promotion?.reconversionType ===
    MODIFY_ORDER_TYPE_PROMOTION.CLICK_AND_COLLECT;

  const hasPromotion =
    (promotion && !isClickAndCollectPromo) || !!marketingCampaignPromoAmount;
  const isDeliveryOrder = orderType === ORDER_TYPE_DELIVERY;

  const hasFee =
    (showFees && (isDeliveryOrder || managementFee > 0)) ||
    isClickAndCollectPromo;

  const activePromo = !commande ? getActiveUserPromotion() : null;
  const promoDeleteProps = {
    deletable: !!activePromo,
    nbUsed: activePromo?.nbUsed,
    promoPath: activePromo?.promoPath,
  };

  const renderPromotion = (promo: any) => {
    if (!promo) return null;

    const isPercentage =
      typeof promo.type === 'string' &&
      promo.type.includes(PROMOTION_PERCENTAGE);

    const promoType =
      isPercentage && !isLoading
        ? promo.type
        : !isLoading && typeof promo.type === 'number'
          ? displayPriceFormat(promo.type)
          : null;

    return (
      promoType && (
        <OrderPromotion
          promotion={promo}
          commande={commande}
          promotionType={promoType}
          prefix='-'
          isLoading={isLoading}
          {...promoDeleteProps}
        />
      )
    );
  };

  return (
    <>
      <SkeletonRow
        label='Sous total'
        amount={displayPriceAsync(subtotalBeforeDiscount)}
        isLoading={isLoading}
        style={{ color: 'unset' }}
      />
      {hasPromotion && (
        <>
          {promotion && !isClickAndCollectPromo && renderPromotion(promotion)}

          {!!remainingMarketingDiscountAmount && (
            <SkeletonRow
              label={
                <div style={{ lineHeight: 1.2 }}>
                  {commande ? 'Remise' : getTitleFromOffer(thresholdOffer)}
                </div>
              }
              isLoading={isLoading}
              amount={`- ${displayPriceFormat(
                remainingMarketingDiscountAmount,
                false,
                true,
              )}`}
              style={{ color: DISHOP_GREEN }}
            />
          )}

          <SkeletonRow
            label='Sous total après remise'
            amount={displayPriceAsync(subtotalAfterDiscount)}
            isLoading={isLoading}
            style={{ color: 'unset' }}
          />
          <DashedLine />
        </>
      )}

      {hasFee && (
        <>
          {((showFees && isDeliveryOrder) || isClickAndCollectPromo) && (
            <SkeletonRow
              label='Livraison'
              amount={displayPriceAsync(userServicePrice)}
              isLoading={isLoading}
              style={{ color: 'unset' }}
            />
          )}

          {showFees && managementFee > 0 && (
            <SkeletonRow
              label='Frais et Services'
              amount={displayPriceAsync(managementFee)}
              isLoading={isLoading}
            />
          )}

          <DashedLine />
        </>
      )}

      {deliveryIsFree && (
        <>
          <OrderPromotion
            promotion={promotion}
            commande={commande}
            promotionType={displayPriceAsync(userServicePrice)}
            prefix='-'
            isLoading={isLoading}
            freeDelivery={deliveryIsFree}
            {...promoDeleteProps}
          />
          <DashedLine />
        </>
      )}

      {promotion && isClickAndCollectPromo && (
        <>
          {renderPromotion(promotion)}
          <DashedLine />
        </>
      )}

      <div
        className='row'
        style={{ paddingTop: 5 }}
      >
        <SkeletonRow
          label={<h4>Total</h4>}
          amount={
            <div style={{ fontSize: UNIT * 1.3, fontWeight: 'bold' }}>
              {displayPriceAsync(totalPriceOrder)}
            </div>
          }
          isLoading={isLoading}
        />
      </div>
    </>
  );
}
